import { useEffect, useRef, useState } from "react";
import { Loader2, Lock, ShieldCheck } from "lucide-react";
import api, { apiError, money2 } from "@/lib/api";

const SDK = {
  sandbox: "https://sandbox.web.squarecdn.com/v1/square.js",
  production: "https://web.squarecdn.com/v1/square.js",
};

function loadSdk(src) {
  if (window.Square) return Promise.resolve();
  const existing = document.querySelector(`script[src="${src}"]`);
  if (existing) return new Promise((res, rej) => { existing.addEventListener("load", res); existing.addEventListener("error", rej); });
  return new Promise((res, rej) => {
    const s = document.createElement("script");
    s.src = src;
    s.onload = res;
    s.onerror = () => rej(new Error("Square could not be reached. Check your connection and try again."));
    document.head.appendChild(s);
  });
}

const newKey = () =>
  (window.crypto?.randomUUID?.() || `k-${Date.now()}-${Math.random().toString(36).slice(2)}`).slice(0, 45);

/** Embedded Square checkout. Amount is re-validated server side against the invoice balance. */
export default function SquareCheckout({ invoice, amount, onPaid, configPath = "/payments/config", payPath }) {
  const cardRef = useRef(null);
  const wallets = useRef({});
  const attemptKey = useRef(newKey());
  const amountRef = useRef(amount);
  amountRef.current = amount;

  // Keep the wallet sheets in step when the client edits the amount after checkout loaded.
  useEffect(() => {
    try {
      wallets.current.request?.update?.({
        total: { amount: String(amount.toFixed(2)), label: invoice.number },
      });
    } catch { /* wallet not available */ }
  }, [amount, invoice.number]);
  const [config, setConfig] = useState(null);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [available, setAvailable] = useState({ apple: false, google: false, cashapp: false, ach: false });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(configPath);
        if (!alive) return;
        setConfig(data);
        if (!data.configured) return;
        await loadSdk(SDK[data.environment === "production" ? "production" : "sandbox"]);
        const payments = window.Square.payments(data.application_id, data.location_id);
        const card = await payments.card({
          style: {
            input: { color: "#141414", fontSize: "15px", backgroundColor: "#FFFFFF" },
            ".input-container": { borderColor: "#E3E1DA", borderRadius: "2px" },
            ".input-container.is-focus": { borderColor: "#BEC221" },
            ".input-container.is-error": { borderColor: "#E07A5F" },
            ".message-text.is-error": { color: "#A8492E" },
          },
        });
        if (!alive) return;
        await card.attach(cardRef.current);
        wallets.current = { payments, card };
        setReady(true);
        const req = payments.paymentRequest({
          countryCode: data.country || "US",
          currencyCode: data.currency || "USD",
          total: { amount: String(amount.toFixed(2)), label: invoice.number },
        });
        wallets.current.request = req;
        const found = { apple: false, google: false, cashapp: false, ach: false };
        try { wallets.current.apple = await payments.applePay(req); found.apple = true; } catch { /* unsupported */ }
        try {
          wallets.current.google = await payments.googlePay(req);
          await wallets.current.google.attach("#sq-google-pay");
          found.google = true;
        } catch { /* unsupported */ }
        try {
          wallets.current.cashapp = await payments.cashAppPay(req, {
            redirectURL: window.location.href, referenceId: invoice.number,
          });
          await wallets.current.cashapp.attach("#sq-cash-app");
          wallets.current.cashapp.addEventListener("ontokenization", (e) => {
            const t = e?.detail?.tokenResult;
            if (t?.status === "OK") submit(t.token, null, "Cash App Pay");
          });
          found.cashapp = true;
        } catch { /* unsupported */ }
        try {
          wallets.current.ach = await payments.ach({ redirectURI: window.location.href, transactionId: attemptKey.current });
          wallets.current.ach.addEventListener("ontokenization", (e) => {
            const t = e?.detail?.tokenResult;
            if (t?.status === "OK") submit(t.token, null, "Bank transfer");
          });
          found.ach = true;
        } catch { /* unsupported */ }
        if (alive) setAvailable(found);
      } catch (e) {
        if (alive) setError(e.message || "Checkout could not start.");
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (token, verificationToken, label) => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post(payPath || `/invoices/${invoice.id}/pay`, {
        source_id: token,
        amount: amountRef.current,
        idempotency_key: attemptKey.current,
        verification_token: verificationToken || undefined,
        method_label: label,
      });
      attemptKey.current = newKey();
      onPaid(data);
    } catch (e) {
      const status = e?.response?.status;
      // Rotate only after a definite rejection. A 5xx may mean the charge actually landed,
      // so the same key must be replayed rather than starting a fresh attempt.
      if (status && status < 500) {
        attemptKey.current = newKey();
        setError(apiError(e));
      } else {
        setError(status === 502
          ? apiError(e)
          : "We couldn’t complete that payment just now. Please check your invoice before trying again.");
      }
    } finally {
      setBusy(false);
    }
  };

  const payCard = async () => {
    setBusy(true);
    setError("");
    try {
      const result = await wallets.current.card.tokenize();
      if (result.status !== "OK") throw new Error(result.errors?.[0]?.message || "Please check your card details.");
      let verification;
      try {
        const v = await wallets.current.payments.verifyBuyer(result.token, {
          amount: String(amountRef.current.toFixed(2)),
          currencyCode: config.currency || "USD",
          intent: "CHARGE",
          billingContact: {},
        });
        verification = v?.token;
      } catch { /* verification is optional outside SCA regions */ }
      await submit(result.token, verification, result.details?.card?.brand || "Card");
    } catch (e) {
      setError(e.message || "We could not process that card.");
      setBusy(false);
    }
  };

  const payWallet = async (name, label) => {
    const m = wallets.current[name];
    if (!m) return;
    setBusy(true);
    setError("");
    try {
      const result = await m.tokenize();
      if (result.status !== "OK") throw new Error(result.errors?.[0]?.message || "Payment was not completed.");
      await submit(result.token, null, label);
    } catch (e) {
      setError(e.message || "Payment was not completed.");
      setBusy(false);
    }
  };

  const payAch = async () => {
    setBusy(true);
    setError("");
    try {
      await wallets.current.ach.tokenize({
        accountHolderName: invoice.client_name || "Account holder",
        intent: "CHARGE",
        amount: String(amountRef.current.toFixed(2)),
        currency: config.currency || "USD",
      });
    } catch (e) {
      setError(e.message || "Bank transfer was not completed.");
      setBusy(false);
    }
  };

  if (config && !config.configured)
    return (
      <div data-testid="square-not-configured" className="card-surface p-7">
        <h3 className="font-serif text-xl text-ink">Payments aren’t switched on yet</h3>
        <p className="mt-2 text-sm text-stone">
          Your studio is finishing the Square connection. You’ll be able to pay here shortly.
        </p>
      </div>
    );

  return (
    <div className="space-y-6" data-testid="square-checkout">
      <div className="card-surface p-6 sm:p-7">
        <div className="flex items-center justify-between">
          <div className="overline">Card details</div>
          <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] text-fog">
            <Lock className="h-3 w-3" strokeWidth={1.5} /> Secured by Square
          </span>
        </div>
        <div ref={cardRef} id="sq-card" className="mt-5 min-h-[92px]" data-testid="square-card-container" />
        {!ready && !error && (
          <div className="flex items-center gap-2 text-sm text-fog">
            <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} /> Preparing secure checkout…
          </div>
        )}
        {error && (
          <p data-testid="square-payment-error" className="mt-5 rounded-sm bg-[#F6DED6] px-4 py-3 text-sm text-[#A8492E]">
            {error}
          </p>
        )}
        <button
          data-testid="square-pay-card-button"
          onClick={payCard}
          disabled={!ready || busy}
          className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-sm bg-lime px-5 py-3.5 text-sm font-medium text-ink transition-colors hover:bg-lime-dark disabled:cursor-not-allowed disabled:opacity-50"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} /> : <ShieldCheck className="h-4 w-4" strokeWidth={1.5} />}
          {busy ? "Processing…" : `Pay ${money2(amount)}`}
        </button>
      </div>

      {(available.apple || available.google || available.cashapp || available.ach) && (
        <div className="card-surface p-6 sm:p-7" data-testid="square-alt-methods">
          <div className="overline">Or pay another way</div>
          <div className="mt-5 space-y-3">
            {available.apple && (
              <button data-testid="square-apple-pay" disabled={busy} onClick={() => payWallet("apple", "Apple Pay")}
                className="w-full rounded-sm bg-ink px-5 py-3 text-sm text-white transition-opacity hover:opacity-90 disabled:opacity-50">
                Apple Pay
              </button>
            )}
            <div id="sq-google-pay" data-testid="square-google-pay" className={available.google ? "" : "hidden"} />
            <div id="sq-cash-app" data-testid="square-cash-app" className={available.cashapp ? "" : "hidden"} />
            {available.ach && (
              <button data-testid="square-ach-pay" disabled={busy} onClick={payAch}
                className="w-full rounded-sm border border-line bg-white px-5 py-3 text-sm text-ink transition-colors hover:bg-bone disabled:opacity-50">
                Pay by bank transfer (ACH)
              </button>
            )}
          </div>
          <p className="mt-4 text-[11px] leading-relaxed text-fog">
            Bank transfers can take a few days to clear — your balance updates automatically once Square confirms.
          </p>
        </div>
      )}
    </div>
  );
}
